import React, { useState, useEffect } from 'react';
import {
  Flex,
  Button,
  Heading,
  Box,
  Tooltip,
  useMediaQuery,
} from '@chakra-ui/react';
import AleaFilterIcon from '../../../icons/AleaFilterIcon';
import AleaCalendarIcon from '../../../icons/AleaCalendarIcon';
import FacilityFilter from './FacilityFilter';
import PeriodFilter from './PeriodFilter';
import ResidentFilter from './ResidentFilter';
import AleaHardwareLivingAreaIcon from '../../../icons/AleaHardwareLivingAreaIcon.tsx';
import { useAuth } from '../../../context/AuthContext';
import { FilterType } from '../AnalysisPage';
import { useTranslation } from 'react-i18next';
import AleaDownloadButton, {
  DownloadOption,
} from '../../../components/AleaDownloadButton/AleaDownloadButton';
import { UserRole } from '../../../constants/UserRoles';

interface Facility {
  id: string;
  name: string;
}

interface FilterState {
  facilityId?: string;
  facilityName?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
}

// Type definitions
interface FilterData {
  facilityId?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
  exportType?: 'pdf' | 'csv';
}

interface AnalysisHeaderProps {
  activeFilter: FilterType;
  onFilterClick: (filterType: FilterType) => void;
  onFilterApply: (filterType: FilterType, data: FilterData) => void;
  initialFilterState?: FilterState;
  facilities: Facility[];
}

const AnalysisHeader: React.FC<AnalysisHeaderProps> = ({
  activeFilter,
  onFilterClick,
  onFilterApply,
  initialFilterState = {},
  facilities,
}) => {
  const { t } = useTranslation();
  const [selectedFacilityId, setSelectedFacilityId] = useState<
    string | undefined
  >(initialFilterState.facilityId);
  const { user } = useAuth();

  // Check if screen width is larger than 1200px
  const [isLargerThan1200] = useMediaQuery('(min-width: 1200px)');

  const isOrganisationManager = user?.role === UserRole.ORGANISATION_MANAGER;

  // Update selected values when initialFilterState changes
  useEffect(() => {
    if (initialFilterState.facilityId) {
      if (initialFilterState.facilityId !== '1') {
        setSelectedFacilityId(initialFilterState.facilityId);
      }
    }
  }, [initialFilterState]);

  const filterButtons = [
    {
      key: 'facility',
      icon: <AleaHardwareLivingAreaIcon size="22" />,
      label: t('careHome'),
      disabled: !isOrganisationManager,
    },
    {
      key: 'period',
      icon: <AleaCalendarIcon size="22" />,
      label: t('analyse.zeitraum'),
    },
    {
      key: 'resident',
      icon: <AleaFilterIcon size="36" color="#2B4956" />,
      label: t('analyse.bewohner'),
    },
  ];

  const handleClose = () => {
    onFilterClick(null);
  };

  const handleFacilityApply = (facilityId: string) => {
    setSelectedFacilityId(facilityId);
    onFilterApply('facility', { facilityId });
  };

  const handlePeriodApply = (startDate: string, endDate: string) => {
    onFilterApply('period', { startDate, endDate });
  };

  const handleResidentApply = (
    selectedStations: string[],
    selectedResidents: string[],
    stationNames?: Record<string, string>
  ) => {
    // Create a comma-separated string of station IDs
    const stationIdsString = selectedStations.join(',');
    onFilterApply('resident', {
      stationIds: stationIdsString,
      residentIds: selectedResidents,
      stationNames,
    });
  };

  const handleFilterClick = (filterType: FilterType) => {
    // Don't open facility filter if user is not an organisation manager
    if (filterType === 'facility' && !isOrganisationManager) {
      return;
    }

    onFilterClick(filterType);
  };

  const handleExportPdfClick = () => {
    onFilterApply('download', { exportType: 'pdf' });
  };

  // Download options for AleaDownloadButton (CSV hidden until export is implemented in AnalysisPage).
  const downloadOptions: DownloadOption[] = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: handleExportPdfClick,
    },
  ];

  const renderFilterContent = () => {
    switch (activeFilter) {
      case 'facility':
        return (
          <FacilityFilter
            facilities={facilities}
            onCancel={handleClose}
            onApply={handleFacilityApply}
            initialSelectedId={selectedFacilityId || ''}
          />
        );

      case 'period':
        return (
          <PeriodFilter
            onCancel={handleClose}
            onApply={handlePeriodApply}
            initialStartDate={initialFilterState.startDate}
            initialEndDate={initialFilterState.endDate}
          />
        );

      case 'resident':
        return (
          <ResidentFilter
            onCancel={handleClose}
            onApply={handleResidentApply}
            initialSelectedStationIds={
              initialFilterState.stationIds?.split(',').filter(id => id.trim() !== '') || []
            }
            initialSelectedResidentIds={initialFilterState.residentIds || []}
            selectedFacilityId={selectedFacilityId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box position="relative">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        minHeight="75px"
        pl="25px"
        position="relative"
      >
        <Heading size="lg" fontSize={25} fontWeight={500}>
          {t('general.analyse')}
        </Heading>

        <Flex
          gap={isLargerThan1200 ? '10px' : '5px'}
          position="relative"
          alignItems="center"
        >
          {filterButtons.map((button) => (
            <Tooltip
              key={button.key}
              label={
                button.disabled
                  ? t('general.onlyForOrgManager')
                  : !isLargerThan1200 && button.label
                    ? button.label
                    : ''
              }
              isDisabled={
                button.disabled || (isLargerThan1200 && !button.disabled)
              }
              placement="top"
              hasArrow
            >
              <Button
                onClick={() => handleFilterClick(button.key as FilterType)}
                variant="outline"
                bg={activeFilter === button.key ? '#F3F2F2' : 'white'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={isLargerThan1200 ? '20px' : '0px'}
                px={isLargerThan1200 ? '16px' : '10px'}
                width={!isLargerThan1200 ? '54px' : 'auto'}
                height="50px"
                borderRadius={
                  activeFilter === button.key ? '20px 20px 0 0' : '20px'
                }
                fontSize="20px"
                fontWeight={400}
                lineHeight="100%"
                letterSpacing="0%"
                _hover={{ bg: button.disabled ? 'white' : '#F3F2F2' }}
                isActive={activeFilter === button.key}
                _active={{ bg: button.disabled ? 'white' : '#F3F2F2' }}
                border="0"
                position={activeFilter === button.key ? 'relative' : 'static'}
                zIndex={activeFilter === button.key ? 1 : 'auto'}
                sx={
                  activeFilter === button.key
                    ? {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-10px',
                          left: 0,
                          right: 0,
                          height: '10px',
                          background: '#F3F2F2',
                        },
                      }
                    : {}
                }
                opacity={button.disabled ? 0.5 : 1}
                cursor={button.disabled ? 'not-allowed' : 'pointer'}
              >
                {isLargerThan1200 && button.label}
                {button.icon}
              </Button>
            </Tooltip>
          ))}

          {/* Download button */}
          <Box data-testid="analysis-header-download">
            <AleaDownloadButton options={downloadOptions} />
          </Box>
        </Flex>
      </Flex>

      {/* Filter Content Area */}
      {activeFilter && (
        <Box
          px="25px"
          py="50px"
          bg="#F3F2F2"
          borderRadius="20px"
          mt="-5px"
          position="relative"
        >
          {renderFilterContent()}
        </Box>
      )}
    </Box>
  );
};

export default AnalysisHeader;
